import { useCallback, useContext, useReducer } from 'react';
import { ObjectContext } from '../context/ObjectContext';
import { useHttpClient } from '../../client/HttpClientContext';
import { PropertyValuesCache } from '../../values/PropertyValuesCache';
import { useGetIsUngated } from './useGetIsUngated';
import { useIsMounted } from './useIsMounted';
const cache = new PropertyValuesCache();
const initialState = {
  status: 'UNINITIALIZED',
  loading: false,
  error: false,
  data: undefined
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'PENDING':
      {
        return {
          status: 'PENDING',
          loading: true,
          error: false,
          data: undefined
        };
      }

    case 'SUCCESS':
      {
        return {
          status: 'SUCCESS',
          loading: false,
          error: false,
          data: action.payload
        };
      }

    case 'FAILED':
      {
        return {
          status: 'FAILED',
          loading: false,
          error: true,
          data: undefined
        };
      }

    default:
      {
        const exhaustiveCheck = action;
        throw new Error(`Unhandled action: ${exhaustiveCheck}`);
      }
  }
};

export const useGetAdditionalPropertyValue = () => {
  const objectContext = useContext(ObjectContext);
  const httpClient = useHttpClient();
  const getIsUngated = useGetIsUngated();
  const isMounted = useIsMounted();
  const [state, dispatch] = useReducer(reducer, initialState);
  const {
    getAdditionalPropertyValue
  } = objectContext;
  const {
    loading,
    error,
    data
  } = state;
  return useCallback((propertyName, objectId, objectTypeId) => {
    const additionalPropertyValue = getAdditionalPropertyValue(propertyName, objectId, objectTypeId); // if value returned by the helper is not undefined (a.k.a. unknown), return it.
    // this allows host apps to give us access to draft values that have not yet been saved

    if (additionalPropertyValue !== undefined) {
      return additionalPropertyValue;
    } // return undefined (value for the dependent property is unknown) if:
    //
    // - objectId is unknown. we can't fetch values for objects which haven't been persisted
    // - property values are loading. the hook will rerender once results become available,
    //   which will cause the input to rerender and reflect the fetched value
    // - property values fetch failed. we were unable to fetch values


    if (!objectId || loading || error) {
      return undefined;
    } // if property values have already been fetched, return cached property value.
    // this may be undefined if the object has no value for that property


    if (data) {
      return data[propertyName];
    } // if portal is not ungated for on-demand value fetching, skip the actual fetch.
    // this lets us collect metrics indicating how often we _would_ have fetched a value


    const skip = !getIsUngated || !getIsUngated('CRM:Properties:GetAdditionalValues');
    dispatch({
      type: `PENDING`
    });
    cache.get({
      objectId,
      objectTypeId,
      httpClient,
      skip
    }).then(res => {
      if (!isMounted.current) {
        return;
      }

      dispatch({
        type: `SUCCESS`,
        payload: res
      });
    }).catch(err => {
      if (!isMounted.current) {
        return;
      }

      dispatch({
        type: `FAILED`,
        payload: err
      });
    });
    return undefined;
  }, [getAdditionalPropertyValue, httpClient, getIsUngated, loading, error, data, isMounted]);
}; // exported for unit test usage only

export const _resetCache = () => {
  cache.clear();
};